// 📁 src/utils/auth.ts
// CTO Recovery Prompt — Canonical Frontend Auth Helpers (v2026.01)
//
// Invariants (LOCKED):
// - JWT is the single source of user identity on the frontend.
// - tenant_id is numeric and used ONLY for X-Tenant-ID header.
// - tenant_slug is human-readable and used ONLY for routing.
// - tenant_id must NEVER be reused as a routing slug.
// - Refresh token calls always go through axiosInstance (/api/v1).
// - Reserved route segments must never be treated as tenant slugs.

import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";
import axiosInstance from "@/utils/axiosInstance";

// ============================================================================
// 🔐 JWT payload structure (as issued by backend app/core/security.py)
// ============================================================================
export interface DecodedToken {
  sub: number | string; // user ID
  role: string;         // applicant | ddo | pm | finance | management | client | super_admin
  exp: number;          // expiry (epoch seconds)
  tenant_id?: number | null;
  principal_type?: "staff" | "applicant";
  token_type?: "access" | "refresh";
}

// ============================================================================
// 🔓 Decode access token and validate lifetime + shape
// ============================================================================
export function getUserFromToken(): DecodedToken | null {
  const token = sessionStorage.getItem("access_token");
  if (!token) return null;

  try {
    const decoded: DecodedToken = jwtDecode(token);
    const now = Date.now() / 1000;

    if (!decoded.exp || decoded.exp < now) {
      console.warn("⏰ Token expired");
      return null;
    }

    if (!decoded.role || decoded.sub === undefined || decoded.sub === null) {
      console.warn("🚫 Invalid token payload");
      return null;
    }

    return decoded;
  } catch (err) {
    console.error("❌ Failed to decode token:", err);
    return null;
  }
}

// ============================================================================
// 🧹 Clears all session data (logout or fatal auth error)
// ============================================================================
export function clearSession() {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("tenant_id");
  sessionStorage.removeItem("tenant_slug");
  toast.warning("Session cleared. Please log in again.");
}

// ============================================================================
// 🔁 Refresh access token using refresh_token (tenant-scoped)
// ============================================================================
export async function refreshToken(): Promise<boolean> {
  const refresh_token = sessionStorage.getItem("refresh_token");
  const tenant_id = sessionStorage.getItem("tenant_id");

  if (!refresh_token || !tenant_id) {
    console.warn("🔐 Missing refresh token or tenant ID for refresh");
    return false;
  }

  try {
    const decodedRefresh: DecodedToken = jwtDecode(refresh_token);
    const isApplicantRefresh =
      decodedRefresh.principal_type === "applicant" || decodedRefresh.role === "applicant";
    const tenantSlug = getTenantSlugFromStorage();
    const refreshPath = isApplicantRefresh
      ? `/auth/public/${encodeURIComponent(tenantSlug)}/refresh-token`
      : "/auth/refresh-token";

    // axiosInstance guarantees correct baseURL and X-Tenant-ID header
    const response = await axiosInstance.post(refreshPath, {
      refresh_token,
    });

    if (response.status !== 200) {
      toast.error("Session expired. Please log in again.");
      return false;
    }

    const data = response.data as {
      access_token?: string;
      refresh_token?: string;
    };

    if (!data.access_token || !data.refresh_token) {
      console.error("❌ Invalid refresh token response");
      return false;
    }

    sessionStorage.setItem("access_token", data.access_token);
    sessionStorage.setItem("refresh_token", data.refresh_token);
    toast.success("Session refreshed.");
    return true;
  } catch (err) {
    console.error("🔁 Token refresh error:", err);
    toast.error("Could not refresh session.");
    return false;
  }
}

// ============================================================================
// 🌐 Canonical helpers for tenant context
// ============================================================================

// Numeric tenant_id → used ONLY for X-Tenant-ID header
export function getTenantIdFromStorage(): string | null {
  const id = sessionStorage.getItem("tenant_id");
  return id && id.trim() !== "" ? id : null;
}

// Slug used ONLY for routing (/:tenantSlug/…)
//
// Resolution order (LOCKED):
//  1) sessionStorage.tenant_slug (human-readable, canonical)
//  2) "default" (dev-safe fallback)
//
// ❌ tenant_id MUST NEVER be reused as a slug
// ❌ reserved system segments MUST NEVER be treated as slugs
export function getTenantSlugFromStorage(): string {
  const RESERVED_SEGMENTS = new Set([
    "ddo",
    "pm",
    "pc",
    "finance",
    "management",
    "login",
    "admin",
    "applicant",
    "client",
    "403",
  ]);

  const raw = sessionStorage.getItem("tenant_slug");
  const slug = raw && raw.trim() ? raw.trim() : null;

  if (slug && !RESERVED_SEGMENTS.has(slug)) {
    return slug;
  }

  // Absolute last resort — dev-safe only
  return "default";
}
